// ============================================================================
// Time & Labor → Hours links. ONE place to make, list and revoke the link a
// facility administrator uses to see their building's hours (/hours/:token).
//
// Ben, 2026-10-23 (item 968, design approved on the demo canvas):
//   * ANY full TCS employee can send a facility admin an hours link. "Sales
//     helps Ops. Jack helps everyone. I help everyone." The AM sees their own
//     buildings by default; "All TCS" lets anyone help.
//   * It replaces the panel at the bottom of Workers & caps and the AM's
//     Client views tab (its "who opened it" is the Opens column here).
//   * A building link may be narrowed to one service line (e.g. RMC + Kitchen).
//   * Sub links (a sub owner sees their own crew) stay with HR, executives,
//     superusers and the national AM (TCSD-0545 point 3), at the bottom of this page.
//
// ⛔ THE DATABASE DECIDES who may make or revoke a link
// (rpc_time_create_report_link / rpc_time_revoke_report_link) and which
// buildings each scope covers (get_work_scopes). This screen only asks.
// Hours only — no rate, no amount, anywhere on this page.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { supabase } from '../../shared/supabase';
import { canAssignWorkers, hasFullAmAuthority, isSalesSeat } from '../../shared/role-gates';
import { useWorkScope, WORK_SCOPE_LABEL } from '../../shared/scope/use-work-scope';
import { WorkScopeChips } from '../../shared/scope/work-scope-chips';
import { SmartSelect } from '../../components/smart-select';
import { useTimeTabsNav } from './time-console';
import { fmtWhen } from './console-parts';
import {
  createReportLink,
  revokeReportLink,
  useFacilityOptions,
  useLinkOpens,
  useProviderOptions,
  useReportLinks,
  useServiceLines,
} from './hooks/use-time-data';

/** Sales sees Time & Labor with this one tab (Ben, 2026-10-23). */
const SALES_TABS = [{ id: 'hours-links', label: 'Hours links' }] as const;

function useSalesTabsNav() {
  return { tabs: SALES_TABS, activeId: 'hours-links' as const, onChange: () => undefined };
}

function TabsForRole() {
  const { profile } = useAuth();
  const role = profile?.role ?? 'none';
  // Each hook is called unconditionally; the role picks which bar is drawn.
  // The HR and account-manager bars both come from timeTabsFor().
  const staff = useTimeTabsNav('hours-links');
  const sales = useSalesTabsNav();
  if (isSalesSeat(role)) return <TabBar {...sales} />;
  return <TabBar {...staff} />;
}

const USAGE_LABEL: Record<string, string> = {
  'never opened': 'Never opened',
  'using it': 'Using it',
  'slowing down': 'Slowing down',
  'stopped looking': 'Stopped looking',
};

/** The service lines a building actually has: its billing lines and who works there. */
function useBuildingLines(facilityId: string | null) {
  const [codes, setCodes] = useState<string[]>([]);
  useEffect(() => {
    if (!facilityId) { setCodes([]); return; }
    let cancelled = false;
    void (async () => {
      const [b, w] = await Promise.all([
        supabase.from('facility_billing_lines').select('service_line_code')
          .eq('facility_id', facilityId).eq('status', 'Active').not('service_line_code', 'is', null),
        supabase.from('worker_assignments').select('service_line_code').eq('facility_id', facilityId),
      ]);
      if (cancelled) return;
      if (b.error || w.error) { setCodes([]); return; } // falls back to every line, below
      const set = new Set<string>();
      for (const r of [...(b.data ?? []), ...(w.data ?? [])] as Array<{ service_line_code: string | null }>) {
        if (r.service_line_code) set.add(r.service_line_code);
      }
      setCodes([...set]);
    })();
    return () => { cancelled = true; };
  }, [facilityId]);
  return codes;
}

/** employee_id → name, for "Made by". */
function useMakers(eids: string[]) {
  const key = eids.slice().sort().join(',');
  const [map, setMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) { setMap(new Map()); return; }
    void (async () => {
      const { data, error } = await supabase.from('employees')
        .select('employee_id, employee_name_first, employee_name_last').in('employee_id', ids);
      if (error) return; // "Made by" falls back to the id
      setMap(new Map(((data ?? []) as Array<{ employee_id: string; employee_name_first: string | null; employee_name_last: string | null }>)
        .map((e) => [e.employee_id, [e.employee_name_first, e.employee_name_last].filter(Boolean).join(' ')])));
    })();
  }, [key]);
  return map;
}

export function HoursLinksPage() {
  const { profile } = useAuth();
  const role = profile?.role ?? 'none';
  // Sub links mirror rpc_time_create_report_link's staff test: HR, executives,
  // superusers and has_full_am_authority() — the national AM (TCSD-0545 point 3).
  const canSubLinks = canAssignWorkers(role) || hasFullAmAuthority(role);

  const work = useWorkScope();
  const facilities = useFacilityOptions();
  const serviceLines = useServiceLines();
  const links = useReportLinks();
  const opens = useLinkOpens();
  const providers = useProviderOptions();

  const [building, setBuilding] = useState<string | null>(null);
  const [extra, setExtra] = useState<string[]>([]);
  const [line, setLine] = useState('');
  const [who, setWho] = useState('');
  const [days, setDays] = useState('90');
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<{ who: string; url: string; copied: boolean } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const [subProvider, setSubProvider] = useState('');
  const [subWho, setSubWho] = useState('');
  const [subDays, setSubDays] = useState('90');
  const [subBusy, setSubBusy] = useState(false);

  const display = useMemo(() => new Map(facilities.rows.map((f) => [f.facility_id, f.display])), [facilities.rows]);
  // The client's page carries the building's own name, never the sub in brackets.
  const clientName = useMemo(() => new Map(facilities.rows.map((f) => [f.facility_id, f.name])), [facilities.rows]);
  const subsWithCrew = useMemo(() => providers.filter((p) => p.hasCrew), [providers]);
  const inScope = useMemo(() => {
    if (work.facilityIds === undefined) return null;   // still loading
    if (work.facilityIds === null) return null;        // All TCS
    return new Set(work.facilityIds);
  }, [work.facilityIds]);

  const buildingCodes = useBuildingLines(building);
  const lineOptions = useMemo(() => {
    const have = new Set(buildingCodes);
    const list = have.size > 0 ? serviceLines.filter((l) => have.has(l.service_line_code)) : serviceLines;
    return list;
  }, [buildingCodes, serviceLines]);

  const opensByLink = useMemo(() => {
    const m = new Map<string, { last: string | null; usage: string; opens30: number }>();
    for (const r of opens.rows) {
      if (!m.has(r.link_id)) m.set(r.link_id, { last: r.last_opened_at, usage: r.usage_state, opens30: Number(r.opens_30d) });
    }
    return m;
  }, [opens.rows]);

  const lineLabel = useMemo(() => new Map(serviceLines.map((l) => [l.service_line_code, l.label])), [serviceLines]);
  const makers = useMakers([...new Set(links.links.map((l) => l.created_by_eid).filter((e): e is string => !!e))]);

  const now = Date.now();
  const facilityLinks = links.links.filter((l) => l.link_scope === 'facility');
  const subLinks = links.links.filter((l) => l.link_scope === 'provider');
  const shownLinks = facilityLinks.filter((l) => {
    const fids = links.facilitiesByLink.get(l.link_id) ?? [];
    return !inScope || fids.some((f) => inScope.has(f));
  });

  const chosen = [building, ...extra].filter((x): x is string => !!x);
  const preview = chosen.length === 0
    ? '—'
    : chosen.map((id) => clientName.get(id) ?? id).join(' · ') + (line ? ` — ${lineLabel.get(line) ?? line}` : '');
  const daysN = Number(days);
  const canMake = chosen.length > 0 && who.trim() !== '' && Number.isInteger(daysN) && daysN >= 1 && daysN <= 365;

  async function make() {
    setBusy(true); setFormError(null); setMade(null);
    try {
      const r = await createReportLink('facility', who.trim(), chosen, null, daysN, line || null);
      const url = `${window.location.origin}/hours/${r.token}`;
      let copied = false;
      try { await navigator.clipboard.writeText(url); copied = true; } catch { /* the address is on screen */ }
      setMade({ who: who.trim(), url, copied });
      setWho(''); setExtra([]); setLine('');
      await Promise.all([links.reload(), opens.reload()]);
    } catch (e) {
      setFormError(errorMessage(e, 'The link could not be made.'));
    } finally {
      setBusy(false);
    }
  }

  async function makeSub() {
    setSubBusy(true); setFormError(null); setMade(null);
    try {
      const r = await createReportLink('provider', subWho.trim(), null, subProvider, Number(subDays) || 90);
      const url = `${window.location.origin}/hours/${r.token}`;
      let copied = false;
      try { await navigator.clipboard.writeText(url); copied = true; } catch { /* on screen */ }
      setMade({ who: subWho.trim(), url, copied });
      setSubWho(''); setSubProvider('');
      await links.reload();
    } catch (e) {
      setFormError(errorMessage(e, 'The sub link could not be made.'));
    } finally {
      setSubBusy(false);
    }
  }

  async function revoke(linkId: string) {
    setFormError(null);
    try {
      await revokeReportLink(linkId);
      setConfirmRevoke(null);
      await Promise.all([links.reload(), opens.reload()]);
    } catch (e) {
      setFormError(errorMessage(e, 'The link could not be revoked.'));
    }
  }

  function statusOf(l: { revoked_at: string | null; expires_at: string }) {
    if (l.revoked_at) return 'revoked';
    if (new Date(l.expires_at).getTime() < now) return 'expired';
    return 'live';
  }

  const scopeWord = work.scope ? WORK_SCOPE_LABEL[work.scope] : '';

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Hours links"
        meta="A facility administrator checks their building's hours on their own phone or computer. Make the link here, copy it, and send it from your own email. Hours only, never money. Every open is recorded."
      />
      <div className="mt-3"><TabsForRole /></div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <WorkScopeChips scope={work.scope} options={work.options} onChange={work.setScope} />
        {work.error && <span className="text-xs text-st-danger-fg">{work.error}</span>}
      </div>

      {formError && <div className="mt-3 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{formError}</div>}

      {/* ⛔ No "buildings with no live link" list (Ben, 2026-10-26, #983: "I do
          not need to see the buildings that have no live link. If I need it, I
          will build it."). Make a link comes straight after the scope switch. */}
      {/* ----- make a link ----- */}
      <section className="mt-4 rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <h2 className="text-sm font-semibold text-tcs-text">Make a link</h2>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-1">
            <span className="text-xs font-medium text-tcs-text-secondary">Building</span>
            <div className="mt-1">
              <SmartSelect
                entity="facility"
                value={building}
                onChange={(id) => { setBuilding(id || null); setLine(''); setMade(null); }}
                placeholder="Search buildings…"
                isSelectable={(row) => display.has(row.id) && (!inScope || inScope.has(row.id))}
              />
            </div>
            {extra.map((id) => (
              <div key={id} className="mt-1 flex items-center gap-2 text-xs text-tcs-text">
                <span>+ {display.get(id) ?? id}</span>
                <button type="button" className="text-tcs-text-muted underline" onClick={() => setExtra(extra.filter((x) => x !== id))}>remove</button>
              </div>
            ))}
            {building && (
              <div className="mt-1">
                <SmartSelect
                  entity="facility"
                  value={null}
                  onChange={(id) => { if (id && id !== building && !extra.includes(id)) setExtra([...extra, id]); }}
                  placeholder="+ Add another building (an admin over several)"
                  isSelectable={(row) => display.has(row.id) && (!inScope || inScope.has(row.id)) && row.id !== building && !extra.includes(row.id)}
                />
              </div>
            )}
          </div>
          <label className="block">
            <span className="text-xs font-medium text-tcs-text-secondary">Service line</span>
            <select
              value={line}
              onChange={(e) => setLine(e.target.value)}
              className="mt-1 h-10 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm"
            >
              <option value="">All service lines</option>
              {lineOptions.map((l) => <option key={l.service_line_code} value={l.service_line_code}>{l.label}</option>)}
            </select>
            <span className="mt-1 block text-[11px] text-tcs-text-muted">Leave on "All service lines" for the whole building.</span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-tcs-text-secondary">Who is it for</span>
            <input
              value={who}
              onChange={(e) => setWho(e.target.value)}
              placeholder="Who is this for? e.g. Mike Reynolds (HCA)"
              className="mt-1 h-10 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm"
            />
            <span className="mt-1 block text-[11px] text-tcs-text-muted">Shown in the list and recorded with every open.</span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-tcs-text-secondary">Days it works</span>
            <input
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
              className="mt-1 h-10 w-24 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm"
              aria-label="Days the link works"
            />
            <span className="mt-1 block text-[11px] text-tcs-text-muted">1 to 365.</span>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button size="sm" loading={busy} disabled={!canMake} onClick={() => void make()}>Make &amp; copy link</Button>
          <span className="text-xs text-tcs-text-secondary">Their page will be headed: <b className="text-tcs-text">{preview}</b></span>
        </div>
        {made && (
          <div className="mt-3 rounded-lg border border-st-ok-border bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg">
            <div className="font-semibold">
              Link made for {made.who}{made.copied ? ' and copied' : ''}. It is shown only this once.
            </div>
            <code className="mt-1 block break-all text-[12px]">{made.url}</code>
            <div className="mt-1 text-xs">Nothing here emails it. Paste it into your own email or text. Lost it? Revoke it below and make a new one.</div>
          </div>
        )}
      </section>

      {/* ----- the list ----- */}
      <section className="mt-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Links{scopeWord ? ` — ${scopeWord}` : ''}</h2>
        <div className="mt-2 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">For</th>
                <th className="px-3 py-2 font-semibold">Building — service line</th>
                <th className="px-3 py-2 font-semibold">Made by</th>
                <th className="px-3 py-2 font-semibold">Expires</th>
                <th className="px-3 py-2 font-semibold">Opens</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {shownLinks.map((l) => {
                const st = statusOf(l);
                const fids = links.facilitiesByLink.get(l.link_id) ?? [];
                const o = opensByLink.get(l.link_id);
                const place = fids.map((id) => display.get(id) ?? id).join(' · ')
                  + (l.service_line_code ? ` — ${lineLabel.get(l.service_line_code) ?? l.service_line_code}` : ' — all lines');
                return (
                  <tr key={l.link_id} className="border-t border-tcs-border bg-tcs-raised">
                    <td className="px-3 py-2 font-medium text-tcs-text">{l.label}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{place}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{(l.created_by_eid && makers.get(l.created_by_eid)) || l.created_by_eid || '—'}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{fmtWhen(l.expires_at)}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">
                      {o ? `${USAGE_LABEL[o.usage] ?? o.usage}${o.last ? ` · last ${fmtWhen(o.last)}` : ''}` : 'Never opened'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={
                        st === 'live'
                          ? 'rounded-full border border-st-ok-border bg-st-ok-bg px-2 py-0.5 text-[11px] font-semibold text-st-ok-fg'
                          : st === 'expired'
                            ? 'rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[11px] font-semibold text-st-warn-fg'
                            : 'rounded-full border border-tcs-border bg-tcs-surface px-2 py-0.5 text-[11px] font-semibold text-tcs-text-muted'
                      }>{st}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {st === 'live' && confirmRevoke !== l.link_id && (
                        <button type="button" onClick={() => setConfirmRevoke(l.link_id)}
                          className="rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-1.5 text-xs font-semibold text-st-danger-fg hover:opacity-80">
                          Revoke
                        </button>
                      )}
                      {st === 'live' && confirmRevoke === l.link_id && (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-st-danger-fg">Stop it now?</span>
                          <Button size="sm" variant="destructive" onClick={() => void revoke(l.link_id)}>Yes, revoke</Button>
                          <Button size="sm" variant="secondary" onClick={() => setConfirmRevoke(null)}>Keep</Button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {shownLinks.length === 0 && !links.loading && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-tcs-text-muted">
                  No links {scopeWord ? `in ${scopeWord}` : ''} yet. Make one above when a facility admin asks to see their hours.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {(links.error || opens.error) && <div className="mt-2 text-sm text-st-danger-fg">{links.error ?? opens.error}</div>}
      </section>

      {/* ----- sub links: HR, executives, superusers ----- */}
      {canSubLinks && (
        <section className="mt-6 rounded-xl border border-tcs-border bg-tcs-raised p-4">
          <h2 className="text-sm font-semibold text-tcs-text">Sub links (HR, executives and superusers)</h2>
          <p className="mt-0.5 text-xs text-tcs-text-secondary">
            Shows a sub owner their own crew&rsquo;s hours wherever they worked. It cannot be narrowed to a service line.
          </p>
          {subsWithCrew.length === 0 ? (
            <div className="mt-2 rounded-lg border border-tcs-border bg-tcs-surface px-3 py-2 text-sm text-tcs-text-secondary">
              No sub has hourly workers recorded as theirs yet. HR records who employs each worker, and a sub
              appears here once they do.
            </div>
          ) : (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <select value={subProvider} onChange={(e) => setSubProvider(e.target.value)}
              className="h-9 max-w-[260px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Sub for this link">
              <option value="">Sub…</option>
              {subsWithCrew.map((p) => <option key={p.provider_id} value={p.provider_id}>{p.display}</option>)}
            </select>
            <input value={subWho} onChange={(e) => setSubWho(e.target.value)} placeholder="Who is this for?"
              className="h-9 w-64 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Who the sub link is for" />
            <input value={subDays} onChange={(e) => setSubDays(e.target.value.replace(/\D/g, ''))}
              className="h-9 w-20 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Days the sub link works" />
            <Button size="sm" loading={subBusy} disabled={!subProvider || subWho.trim() === ''} onClick={() => void makeSub()}>
              Make &amp; copy link
            </Button>
          </div>
          )}
          <div className="mt-3 overflow-x-auto rounded-xl border border-tcs-border">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {subLinks.map((l) => {
                  const st = statusOf(l);
                  return (
                    <tr key={l.link_id} className="border-t border-tcs-border bg-tcs-raised first:border-t-0">
                      <td className="px-3 py-2 font-medium text-tcs-text">{l.label}</td>
                      <td className="px-3 py-2 text-tcs-text-secondary">Sub: {providers.find((p) => p.provider_id === l.provider_id)?.display ?? l.provider_id}</td>
                      <td className="px-3 py-2 text-tcs-text-secondary">{fmtWhen(l.expires_at)}</td>
                      <td className="px-3 py-2 text-tcs-text-secondary">{st}</td>
                      <td className="px-3 py-2 text-right">
                        {st === 'live' && (
                          <Button size="sm" variant="secondary" onClick={() => void revoke(l.link_id)}>Revoke</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {subLinks.length === 0 && (
                  <tr><td className="px-3 py-4 text-center text-sm text-tcs-text-muted">No sub links yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
